/** @format */

import {styled} from '@mui/material/styles';
import {Box, Button, TextField} from '@mui/material';

export const NoteContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isAggregated',
})<{isAggregated?: boolean}>(({theme, isAggregated}) => ({
  padding: isAggregated ? theme.spacing(2, 4) : theme.spacing(4),
  background: theme.palette.figma_gray_light_4?.main,
  borderRadius: isAggregated ? 0 : theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  width: '350px',
  maxHeight: '500px',
  overflowY: 'auto',
  color: theme.palette.figma_gray_dark_5?.main,
  '&:first-of-type': {
    paddingTop: theme.spacing(4),
  },
}));

export const NoteContainerHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
}));

export const ContainerHeaderNotesCount = styled(Box)(({theme}) => ({
  fontSize: '14px',
  lineHeight: '16px',
  marginTop: theme.spacing(2),
}));

export const AddNewNoteButton = styled(Button)(({theme}) => ({
  padding: theme.spacing(2, 4),
  backgroundColor: '#E2E3E9',
  color: theme.palette.figma_gray_dark_5?.main,
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: 400,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  borderRadius: theme.spacing(2),
  cursor: 'pointer',
  maxHeight: '32px',

  '&:hover': {
    background: theme.palette.figma_surface?.light,
  },
  '&:active': {
    background: theme.palette.figma_primary?.light,
  },
}));

export const Note = styled(Box)(({theme}) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  '&:focus-within [data-role="note-header"]': {
    background: theme.palette.figma_primary?.dark,
  },
}));

export const NoteContent = styled(Box)<{disabled?: boolean}>(({disabled}) => ({
  display: 'flex',
  flexDirection: 'column',
  ...(disabled && {
    userSelect: 'none',
    pointerEvents: 'none',
  }),
}));

export const NoteHeader = styled(Box)(({theme}) => ({
  background: '#CCA941',
  color: theme.palette.figma_gray_light_4?.main,
  borderRadius: theme.spacing(2, 2, 0, 0),
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: '12px',
  lineHeight: '14px',
  fontWeight: 400,
}));

export const IconButton = styled(Button)(({theme}) => ({
  padding: theme.spacing(1.5),
  minWidth: 'unset',
  '.MuiSvgIcon-root': {
    height: '16px',
    width: '16px',
  },
}));

export const NoteBody = styled(TextField)(({theme}) => ({
  background: '#CCA94133',
  borderRadius: theme.spacing(0, 0, 2, 2),
  overflow: 'hidden',
  width: '100%',

  '& .MuiInputBase-input': {
    padding: 0,
    color: theme.palette.figma_gray_dark_5?.main,
    cursor: 'pointer',
  },

  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(0, 0, 2, 2),
    cursor: 'pointer',

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.figma_primary?.light,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.figma_primary?.light,
    },
  },
}));

export const NoteBodyWrapper = styled(Box)(() => ({
  position: 'relative',
}));

export const OnDeleteOverlay = styled(Box)(({theme}) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(2),
  zIndex: 2,
  pointerEvents: 'auto',
  borderRadius: theme.spacing(0, 0, 2, 2),
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backdropFilter: 'blur(4px)',
    background: 'rgba(250, 220, 218, 0.75)',
    borderRadius: theme.spacing(0, 0, 2, 2),
  },
}));

export const OverlayContent = styled(Box)(({theme}) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  gap: theme.spacing(2),
  width: '100%',
}));

export const DeleteNoteButton = styled(Box)(({theme}) => ({
  padding: theme.spacing(2, 4),
  backgroundColor: '#C06868',
  color: '#FEFAFA',
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: 400,
  borderRadius: theme.spacing(2),
  cursor: 'pointer',
  flexGrow: 1,
  textAlign: 'center',
  '&:hover': {
    backgroundColor: '#EC8786',
  },
  '&:active': {
    backgroundColor: '#F2A4A2',
  },
}));

export const CancelDeleteButton = styled(Box)(({theme}) => ({
  padding: theme.spacing(2, 4),
  color: theme.palette.figma_primary?.dark,
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: 400,
  borderRadius: theme.spacing(2),
  cursor: 'pointer',
  flexShrink: 0,
  textAlign: 'center',
  '&:hover': {
    background:
      'linear-gradient(0deg, var(--primary-hover-primary, rgba(88, 172, 242, 0.20)) 0%, var(--primary-hover-primary, rgba(88, 172, 242, 0.20)) 100%)',
  },
  '&:active': {
    backgroundColor: theme.palette.figma_primary?.light,
    color: theme.palette.figma_good?.main,
  },
}));

export const AggregatedContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '500px',
  overflowY: 'auto',
  borderRadius: theme.spacing(2),
}));

export const NotesContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));
